import rounded from "../../../assets/images/svg/uploadRounded.svg";
import { ReactComponent as Close } from "../../../assets/images/icons/CloseSquare.svg";
import { ReactComponent as ChevronDown } from "../../../assets/images/icons/ChevronDown.svg";
import { ReactComponent as ChevronUp } from "../../../assets/images/icons/ChevronUp.svg";
import { useEffect, useState } from "react";
import ConfirmationModal from "../confirmationModal/confirmationModal.index";
import {
    CompleteFileQueueStatus,
    CompleteFileQueueType,
    FileType,
    FileUploadStatus,
    FolderUploadStructureType,
    ResourceAccessEnum,
    ResourceType
} from "../../../interfaces/general";
import Overwrite from "./overwrite/overwrite.index";
import { ActionEnum } from "./overwrite/overwrite.interface";
import { UnitConvertStandard, allFormatRegex, getFileManagerParentId, getWebEnvConfig, isTouchScreen, secondsToHms } from "../../../scripts/helpers";
import { get, post, responseValidator } from "../../../scripts/api";
import API from "../../../urls/api";
import { v4 as uuidv4 } from "uuid";
import ReactDOM from "react-dom";
import { getObjects, updateQuota } from "../fileManagers/fileManager.function";
import {
    handleAddToResources,
    handleSelectResources,
    handleSetResources,
    handleSetVersionResources,
    handleUpdateResources
} from "../fileManagers/fileManager.actions";
import NewUploadButton from "../fileManagers/common/newUploadButton/newUploadButton.index";
import { abortUpload, completeUpload, getUploadPercent, handleSetFileUploadState, handleSetFolderUploadState } from "../../../scripts/upload";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import PATH from "../../../urls/path";
import { useLocation, useNavigate } from "react-router-dom";
import Draggable from "react-draggable";
import Collapsible from "react-collapsible";
import folderName from "../../../assets/images/fileManager/Folder.svg";
import arrowDown from "../../../assets/images/icons/CaretDown.svg";
import UploadItem from "./uploadItem/uploadItem.index";
import successUpload from "../../../assets/images/svg/successUpload.svg";
import EnoughSpace from "./enoughSpace/enoughSpace.index";
import { toast } from "mobin-toast";
import { toastMessages } from "../../../scripts/toastMessages";
import { shallowEqual } from "react-redux";
import store from "../../../redux/store";
import { setReduxFilesToUpload } from "../../../redux/slices/uploadSlice";

function Upload() {
    const location = useLocation();
    const { resources, versionResources, selectedResources } = useAppSelector(
        ({ fileManager: { resources, versionResources, selectedResources } }) => ({ resources, versionResources, selectedResources }),
        shallowEqual
    );
    const { folder, file } = useAppSelector(({ upload: { folder, file } }) => ({ folder, file }), shallowEqual);
    const [filesToUpload, setFilesToUpload] = useState<FileType[]>([]);
    const [currentData, setCurrentData] = useState<ResourceType[]>([]); // resources
    const [overwriteModalStatus, setOverwriteModalStatus] = useState(false);
    const [expand, setExpand] = useState(true);
    const [abort, setAbort] = useState(false);
    const [cancelModalStatus, setCancelModalStatus] = useState(false);
    const [uploadSpeeds, setUploadSpeeds] = useState<{ id: string; speed: number }[]>([]);
    const [uploadedSize, setUploadedSize] = useState<{ id: string; total: number }[]>([]);
    const [totalUploadedSize, setTotalUploadedSize] = useState(0);
    const [uploadFolderStructure, setUploadFolderStructure] = useState<FolderUploadStructureType[]>([]);
    const [arrowDirection, setArrowDirection] = useState<boolean>(false);
    const [completeUploadQueue, setCompleteUploadQueue] = useState<CompleteFileQueueType[]>([]);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
        const unloadCallback = (event: any) => {
            if (
                filesToUpload.filter(
                    (item) =>
                        item.status === FileUploadStatus.PROGRESS || item.status === FileUploadStatus.QUEUE || item.status === FileUploadStatus.PAUSED
                ).length > 0
            ) {
                event.preventDefault();
                event.returnValue = "";
                return "";
            }
        };
        window.addEventListener("beforeunload", unloadCallback);
        return () => window.removeEventListener("beforeunload", unloadCallback);
    }, [filesToUpload]); // refresh check

    useEffect(() => {
        if (folder.files.length > 0) handleUploadFolder(folder.files, folder.parent, folder.hash);
    }, [folder]); // start folders upload

    useEffect(() => {
        if (file.files.length > 0) handleUpload(file.files, file.parent, undefined, file.hash, file.versionGroup, file.versionByEdit);
    }, [file]); // start files upload

    useEffect(() => {
        // sync redux
        dispatch(setReduxFilesToUpload(filesToUpload));
        const inProgressUploadsCount = filesToUpload.filter((file) => file.status === FileUploadStatus.PROGRESS).length;
        filesToUpload
            .filter((file) => file.status === FileUploadStatus.QUEUE)
            .slice(0, Number(getWebEnvConfig("maxUploadCount")) - inProgressUploadsCount)
            .forEach((item) => {
                onPlayHandler(item.id);
            });
    }, [filesToUpload]); // upload queue

    useEffect(() => {
        if (filesToUpload.length === 0) {
            setAbort(false);
            handleResetUploaderInformation();
        }
    }, [filesToUpload]); // abort reset

    useEffect(() => {
        // Folder Upload
        handleStartFolderUpload();
    }, [uploadFolderStructure]);

    const handleResetUploaderInformation = () => {
        if (filesToUpload.length > 0) {
            setFilesToUpload((prev) => prev.filter((item) => item.status !== FileUploadStatus.QUEUE));
        }
        handleSetFileUploadState({ files: [] });
        handleSetFolderUploadState({ files: [] });
        setUploadFolderStructure([]);
        setUploadSpeeds([]);
        setUploadedSize([]);
    };

    const handleCancelAllUploads = () => {
        setAbort(true);
        handleResetUploaderInformation();
        setCancelModalStatus(false);
    };

    const handleUpload = async (
        files: File[],
        parent: any | undefined = getFileManagerParentId(),
        folderName: string | undefined = undefined,
        hash?: string,
        versionGroup?: string,
        versionByEdit?: boolean
    ) => {
        if (files.length > 0) {
            const data = await getObjects(hash ? hash : parent);
            if (data) {
                setCurrentData(data);
                files.forEach((file) => {
                    if (file.size !== 0 && file.size < +getWebEnvConfig("MAX_UPLOAD_FILE_SIZE")) {
                        const format = file.name ? file.name.match(allFormatRegex) : "";
                        let filename = file.name;
                        if (format) {
                            filename = file.name.slice(0, format["index"]);
                            filename =
                                filename.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, "").length === 0
                                    ? `${uuidv4()}${format[0]}`
                                    : `${filename.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, "")}${format[0]}`;
                        }
                        const newFile: FileType = {
                            id: uuidv4(),
                            file: file,
                            name: filename,
                            overwrite: false,
                            status: FileUploadStatus.PENDING,
                            parentId: parent,
                            folderName,
                            hash,
                            versionGroup,
                            versionByEdit
                        };
                        newFile.status = FileUploadStatus.QUEUE;
                        setFilesToUpload((prevState) => [...prevState, newFile]);
                    } else if (file.size > +getWebEnvConfig("MAX_UPLOAD_FILE_SIZE")) {
                        toast({ type: "error", title: toastMessages(98) });
                    }
                });
            }
        }
    };

    const handleChangeStatus = (id: string, status: FileUploadStatus, failStatus?: number) => {
        setFilesToUpload((prev) => {
            return prev.map((item) => {
                if (item.id === id) {
                    return { ...item, status, failStatus };
                } else {
                    return item;
                }
            });
        });
    };

    const onSuccessHandler = (id: string) => {
        handleChangeStatus(id, FileUploadStatus.DONE);
        setUploadSpeeds((prev) => prev.filter((item) => item.id !== id));
    };

    const onFailHandler = (id: string, failStatus?: number) => {
        handleChangeStatus(id, FileUploadStatus.FAILED, failStatus);
        setUploadSpeeds((prev) => prev.filter((item) => item.id !== id));
    };

    const onCancelHandler = (id: string, uploadInformation: any) => {
        const abort = () => {
            abortUpload(
                () => {
                    setFilesToUpload((prev) => prev.filter((item) => item.id !== id));
                },
                { key: uploadInformation?.key, upload_id: uploadInformation?.upload_id }
            );
            setUploadSpeeds((prev) => prev.filter((item) => item.id !== id));
        };
        const completeRequest = completeUploadQueue.find((item) => item.id === id);
        if (!completeRequest) {
            abort();
            return;
        }
        if (completeRequest && completeRequest.status === CompleteFileQueueStatus.QUEUE) {
            setCompleteUploadQueue((prev) => prev.filter((item) => item.id !== id));
            abort();
        }
    };

    const onPauseHandler = (id: string) => {
        handleChangeStatus(id, FileUploadStatus.PAUSED);
    };

    const onPlayHandler = (id: string) => {
        const inProgressUploadsCount = filesToUpload.filter((file) => file.status === FileUploadStatus.PROGRESS).length;
        if (Number(getWebEnvConfig("maxUploadCount")) > inProgressUploadsCount) {
            handleChangeStatus(id, FileUploadStatus.PROGRESS);
        } else {
            handleChangeStatus(id, FileUploadStatus.QUEUE);
        }
    };

    const onOverwriteHandler = async (action: ActionEnum, doForAll: boolean, id: string | null) => {
        const parent = filesToUpload.find((file) => file.id === id);
        const data = await getObjects(location.pathname === PATH.shared ? parent?.hash : parent?.parentId);
        if (doForAll) {
            filesToUpload
                .filter((file) => file.status === FileUploadStatus.PENDING)
                .forEach((item) => {
                    handleOverwriteAction(item.id, action, data);
                });
        } else {
            if (id) handleOverwriteAction(id, action, data);
        }
    };

    const handleOverwriteAction = (id: string, action: ActionEnum, data: ResourceType[]) => {
        const index = filesToUpload.findIndex((item) => item.id === id);
        filesToUpload[index].status = FileUploadStatus.QUEUE;
        if (action === ActionEnum.REPLACE) {
            filesToUpload[index].overwrite = true;
        } else {
            let counter = 1;
            const filenameExtension = filesToUpload[index].name.split(".").pop();
            const filename = filesToUpload[index].name.slice(0, filesToUpload[index].name.lastIndexOf("."));
            while (data?.map((item) => item.name).includes(`${filename}-Copy(${counter}).${filenameExtension}`)) {
                counter++;
            }
            filesToUpload[index].name = `${filename}-Copy(${counter}).${filenameExtension}`;
        }
        setFilesToUpload([...filesToUpload]);
    };

    const onChunkUpload = (chunkCount: { id: string; total: number }, action: string) => {
        if (action === "+") {
            if (uploadedSize.find((item) => item.id === chunkCount.id)) {
                setUploadedSize(
                    uploadedSize.map((item) => {
                        if (item.id === chunkCount.id) {
                            return { ...item, total: chunkCount.total };
                        } else {
                            return item;
                        }
                    })
                );
            } else {
                setUploadedSize([...uploadedSize, chunkCount]);
            }
        } else {
            setUploadedSize(uploadedSize.filter((item) => item.id === chunkCount.id));
        }
    };

    const handleTransferUploadSpeed = (id: string, speed: number) => {
        if (uploadSpeeds.find((item) => item.id === id)) {
            setUploadSpeeds((prev) => {
                return prev.map((item) => {
                    if (item.id === id) {
                        return { ...item, speed: speed };
                    } else {
                        return item;
                    }
                });
            });
        } else {
            setUploadSpeeds((prev) => [...prev, { id, speed }]);
        }
    };

    const calculateAllChunks = () => {
        return filesToUpload.reduce((a, b) => a + b.file.size, 0);
    };

    const calculateUploadingSize = () => {
        return (
            calculateAllChunks() -
            filesToUpload.filter((item) => item.status === FileUploadStatus.PAUSED).reduce((t, c) => t + c.file.size, 0) -
            totalUploadedSize
        );
    };

    const calculateRemainingUploadTime = () => {
        return calculateUploadingSize() / uploadSpeeds.reduce((total, current) => total + current.speed, 0);
    };

    const calculateTotalSpeed = () => {
        return uploadSpeeds.reduce((total, current) => total + current.speed, 0);
    };

    const calculateUploadPercent = () => {
        const totalSize = calculateAllChunks();
        return filesToUpload.filter((file) => file.status === FileUploadStatus.FAILED || file.status === FileUploadStatus.DONE).length ===
            filesToUpload.length ? null : (
            <small className="text-secondary-200 text-[10px]">{`(${getUploadPercent(
                totalSize,
                uploadedSize.map((item) => item.total).reduce((a, b) => a + b, 0)
            )} بارگذاری شده)`}</small>
        );
    };

    const handleSetUploadsState = (data: FolderUploadStructureType, parent: number, status: FileUploadStatus, folderName: string, hash?: string) => {
        // Folder Upload
        const format = data.name ? data.name.match(allFormatRegex) : "";
        let filename = data.name;
        if (format) {
            filename = data.name.slice(0, format["index"]);
            filename =
                filename.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, "").length === 0
                    ? `${uuidv4()}${format[0]}`
                    : `${filename.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, "")}${format[0]}`;
        }
        const file: FileType = {
            id: uuidv4(),
            file: data.file as File,
            name: filename,
            status: status,
            parentId: parent,
            overwrite: false,
            folderName: folderName,
            hash: hash
        };
        setFilesToUpload((prevState) => [...prevState, file]);
    };

    const handleUploadFolder = (files: File[], parent: number | undefined = getFileManagerParentId(), hash?: string) => {
        // Folder Upload
        if (files) {
            const myFiles = Array.prototype.slice.call(files);
            const tree: FolderUploadStructureType[] = [];
            myFiles
                .filter((item) => item.size !== 0)
                .forEach((file) => {
                    let current = tree;
                    const path = file.filepath ? file.filepath : file.webkitRelativePath;
                    path.split("/").forEach((name: string) => {
                        let existing = current.find((item) => item.name === name);
                        if (!existing) {
                            existing = { name, children: [], parent, hash, file: file.name === name ? file : undefined };
                            current.push(existing);
                        }
                        current = existing.children;
                    });
                });
            setUploadFolderStructure(tree);
        }
    };

    const handleStartFolderUpload = async () => {
        // Folder Upload
        if (uploadFolderStructure.length > 0) {
            const data = await getObjects(
                Object.values(uploadFolderStructure)[0].hash
                    ? Object.values(uploadFolderStructure)[0].hash
                    : Object.values(uploadFolderStructure)[0].parent
            );
            Object.values(uploadFolderStructure).forEach((structure) => {
                if (structure) {
                    let folderName =
                        structure.name.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, "").length === 0
                            ? uuidv4()
                            : structure.name.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, "");
                    post(
                        (data, status) => {
                            if (responseValidator(status)) {
                                if (resources) {
                                    if ((!getFileManagerParentId() && data.parent === null) || data.parent === getFileManagerParentId()) {
                                        handleAddToResources({ ...data, shareData: { access: ResourceAccessEnum.WRITE } });
                                    }
                                }
                                structure.children.forEach((child: FolderUploadStructureType) => {
                                    if (child.file) handleSetUploadsState(child, data.id, FileUploadStatus.QUEUE, folderName, data.obj_hash);
                                });
                                handleCreateFolders(
                                    structure.children.filter((item) => !item.file),
                                    data.id,
                                    folderName,
                                    data.obj_hash
                                );
                            }
                        },
                        API.fileManager.createFolder(),
                        {
                            name: folderName,
                            parent: structure.parent
                        }
                    );
                } else {
                    toast({ title: toastMessages(61), type: "error" });
                }
            });
        }
    };

    const handleCreateFolders = (children: FolderUploadStructureType[], parent: number, folderName: string, hash?: string) => {
        // Folder Upload
        children.forEach((child) => {
            if (child.children.length !== 0) {
                post(
                    (data, status) => {
                        if (responseValidator(status)) {
                            child.children.forEach((item) => {
                                if (item.file) handleSetUploadsState(item, data.id, FileUploadStatus.QUEUE, folderName, hash);
                            });
                            handleCreateFolders(
                                child.children.filter((item) => !item.file),
                                data.id,
                                folderName,
                                data.obj_hash
                            );
                        }
                    },
                    API.fileManager.createFolder(),
                    {
                        name:
                            child.name.replace(/[&\/\\#^+$~%'":*?<>{}!@]/g, "").length === 0
                                ? uuidv4()
                                : child.name.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, ""),
                        parent: parent
                    }
                );
            }
        });
    };

    const filesToRender = (items: FileType[]) => {
        return items.filter((item) => item.status !== FileUploadStatus.DONE && item.status !== FileUploadStatus.QUEUE);
    };

    const handleCancelButtonClick = () => {
        if (
            filesToUpload.filter(
                (item) =>
                    item.status === FileUploadStatus.QUEUE || item.status === FileUploadStatus.PAUSED || item.status === FileUploadStatus.PROGRESS
            ).length > 0
        ) {
            setCancelModalStatus(true);
        } else {
            handleResetUploaderInformation();
            setFilesToUpload([]);
        }
    };

    useEffect(() => {
        setTotalUploadedSize(uploadedSize.map((item) => item.total).reduce((a, b) => a + b, 0));
    }, [uploadedSize, filesToUpload]);

    useEffect(() => {
        handleCompleteUpload();
    }, [completeUploadQueue]);

    const handleSetThumb = (data: any) => {
        setTimeout(
            () => {
                if (resources) {
                    get((image, status) => {
                        if (responseValidator(status) && resources) {
                            const resource = { ...data, has_cover: true };
                            const resources = store.getState().fileManager.resources;
                            if (resources) {
                                if (resources.find((item) => item.name === data.name)) {
                                    handleUpdateResources(resource);
                                }
                            }
                        }
                    }, data.download_url + "?thumb=true");
                }
            },
            Number(getWebEnvConfig("thumbnailRequestTime"))
        );
    };

    const handleAddUploadedFileToFileManager = (data: any, shared: boolean, uploadedFile: FileType) => {
        if (resources && data.name) {
            if (shared) {
                if (location.search.startsWith(`?path=${uploadedFile.hash}`)) {
                    handleAddToResources({ ...data, shareData: { access: ResourceAccessEnum.WRITE } });
                    handleSetThumb(data);
                }
            } else {
                if (!uploadedFile.parentId && location.pathname === PATH.files) {
                    handleAddToResources({ ...data, shareData: { access: ResourceAccessEnum.WRITE } });
                    handleSetThumb(data);
                } else {
                    if (location.search === `?path=${uploadedFile.parentId}`) {
                        handleAddToResources({ ...data, shareData: { access: ResourceAccessEnum.WRITE } });
                        handleSetThumb(data);
                    }
                }
            }
        }
    };

    const handleCompleteUpload = () => {
        if (completeUploadQueue.length === 0) return;
        const inProgressComplete = completeUploadQueue.find((item) => item.status === CompleteFileQueueStatus.PROGRESS);
        if (inProgressComplete) return;
        const readyToCompleteFile = completeUploadQueue[0];
        setCompleteUploadQueue((prev) => {
            return prev.map((item) => {
                if (item.uploadedFile.id === readyToCompleteFile.uploadedFile.id) {
                    return {
                        ...item,
                        status: CompleteFileQueueStatus.PROGRESS
                    };
                } else {
                    return item;
                }
            });
        });
        completeUpload(
            (data, status) => {
                setCompleteUploadQueue((prev) => prev.filter((item) => item.uploadedFile.id !== readyToCompleteFile.uploadedFile.id));
                if (responseValidator(status)) {
                    if (selectedResources.map((item) => item.version_group).includes(data.version_group)) {
                        handleSelectResources(
                            selectedResources.map((item) => {
                                if (item.version_group === data.version_group) {
                                    return data;
                                } else {
                                    return item;
                                }
                            })
                        );
                    }
                    // if (location.pathname === PATH.excelEditor()) {
                    //     navigate(PATH.excelEditor(data.version_group, data.parent), { replace: true });
                    // }
                    onSuccessHandler(readyToCompleteFile.uploadedFile.id);
                    updateQuota();
                    if (!readyToCompleteFile.uploadedFile.versionGroup) {
                        if (readyToCompleteFile.uploadedFile.hash) {
                            handleAddUploadedFileToFileManager(data, true, readyToCompleteFile.uploadedFile);
                        } else {
                            handleAddUploadedFileToFileManager(data, false, readyToCompleteFile.uploadedFile);
                        }
                    } else {
                        if (versionResources) {
                            handleSetVersionResources([data, ...versionResources.map((item) => ({ ...item, is_current_version: false }))]);
                        }
                        if (readyToCompleteFile.uploadedFile.versionByEdit && resources)
                            handleSetResources(
                                resources.map((item) => (item.version_group === readyToCompleteFile.uploadedFile.versionGroup ? data : item))
                            );
                    }
                } else {
                    onFailHandler(readyToCompleteFile.uploadedFile.id, 413);
                }
                if (document.querySelectorAll(".upload-input")) {
                    document.querySelectorAll(".upload-input").forEach((el: any) => (el.value = null));
                }
            },
            {
                key: readyToCompleteFile.uploadInformation.key,
                name: readyToCompleteFile.uploadedFile.name,
                parent: readyToCompleteFile.uploadedFile.parentId,
                upload_id: readyToCompleteFile.uploadInformation.upload_id,
                parts: readyToCompleteFile.parts,
                force_overwrite: readyToCompleteFile.uploadedFile.overwrite,
                version_group: readyToCompleteFile.uploadedFile.versionGroup
            }
        );
    };

    const onComplete = (data: CompleteFileQueueType) => {
        setCompleteUploadQueue((prev) => [...prev, data]);
    };

    return ReactDOM.createPortal(
        <>
            <EnoughSpace />
            <Overwrite
                files={filesToUpload.filter((file) => file.status === FileUploadStatus.PENDING)}
                onClose={() => {
                    setFilesToUpload((prevState) => [...prevState.filter((file) => file.status !== FileUploadStatus.PENDING)]);
                    setOverwriteModalStatus(false);
                }}
                status={overwriteModalStatus}
                onSubmit={onOverwriteHandler}
                currentData={currentData}
            />
            {filesToUpload.length > 0 && (
                <Draggable disabled={window.innerWidth < 1024 || !expand} handle=".cursor" bounds="body">
                    <div
                        className={`absolute no-cursor sm:left-4 sm:bottom-4 left-0 bottom-0 sm:w-[400px] max-w-[400px] w-full ${versionResources ? "z-[1000]" : "z-[20]"}`}
                    >
                        {isTouchScreen() && <NewUploadButton className="relative flex justify-end left-0 right-0 top-2" />}
                        <div
                            className={`${expand ? "h-[400px]" : "h-[100px]"} transition-all flex flex-col drop-shadow-xl bg-white rounded-[20px] overflow-hidden animate__animated animate__slideInUp animate__faster`}
                        >
                            <div className={`text-white bg-no-repeat bg-cover bg-scroll bg-bottom cursor ${expand && "lg:cursor-move"}`}>
                                <div className="relative">
                                    <img src={rounded} className="w-full" alt="" />
                                    <div
                                        onClick={() => setExpand(!expand)}
                                        className="flex justify-center absolute bottom-0 w-full h-[40px] z-[10] cursor-pointer"
                                    >
                                        {expand ? (
                                            <ChevronDown className="stroke-white relative top-[10px]" />
                                        ) : (
                                            <ChevronUp className="stroke-white relative top-[10px]" />
                                        )}
                                    </div>
                                    <div className="absolute w-full p-4 -top-1 sm:top-0 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span>
                                                {filesToUpload.filter(
                                                    (file) => file.status === FileUploadStatus.FAILED || file.status === FileUploadStatus.DONE
                                                ).length === filesToUpload.length
                                                    ? `${filesToUpload.filter((file) => file.status === FileUploadStatus.DONE).length} آیتم موفق`
                                                    : `در حال آپلود ${filesToUpload.filter((file) => ![FileUploadStatus.DONE, FileUploadStatus.FAILED].includes(file.status)).length} آیتم`}
                                            </span>
                                            {calculateUploadPercent()}
                                        </div>
                                        <button onClick={handleCancelButtonClick}>
                                            <Close className="stroke-white" />
                                        </button>
                                    </div>
                                    <div className="absolute -bottom-[13px] flex items-center justify-between w-full px-4">
                                        <span className="text-[12px] text-black">
                                            {filesToUpload.filter(
                                                (file) =>
                                                    file.status === FileUploadStatus.FAILED ||
                                                    file.status === FileUploadStatus.DONE ||
                                                    file.status === FileUploadStatus.PAUSED
                                            ).length !== filesToUpload.length
                                                ? secondsToHms(calculateRemainingUploadTime())
                                                    ? secondsToHms(calculateRemainingUploadTime())
                                                    : "در حال محاسبه"
                                                : null}
                                        </span>

                                        <div className="">
                                            {filesToUpload.filter(
                                                (file) =>
                                                    file.status === FileUploadStatus.FAILED ||
                                                    file.status === FileUploadStatus.DONE ||
                                                    file.status === FileUploadStatus.PAUSED
                                            ).length !== filesToUpload.length ? (
                                                UnitConvertStandard(calculateTotalSpeed(), "fa").value ? (
                                                    <span className="flex items-center gap-1 text-[#A0A3BD]">
                                                        <p className="text-[16px]">
                                                            {UnitConvertStandard(calculateTotalSpeed(), "fa").value.toFixed(2)}
                                                        </p>
                                                        <p className="text-[12px]">
                                                            {UnitConvertStandard(calculateTotalSpeed(), "fa").unit} بر ثانیه
                                                        </p>
                                                    </span>
                                                ) : (
                                                    "در حال محاسبه"
                                                )
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {filesToUpload.filter((item) => item.status === FileUploadStatus.DONE).length === filesToUpload.length ? (
                                <div className="flex flex-col gap-4 items-center justify-center flex-1">
                                    <img src={successUpload} alt="success" />
                                    <p>{filesToUpload.filter((item) => item.status === FileUploadStatus.DONE).length} آیتم با موفقیت بارگذاری شد.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="px-4 pt-4 mt-2 overflow-auto mb-4">
                                        <hr />
                                    </div>
                                    <div className="px-4 pb-4 flex-1 bg-white mt-1 overflow-auto">
                                        {Object.values(
                                            filesToUpload
                                                .filter((item) => item.folderName)
                                                .reduce((hash, obj) => {
                                                    // @ts-ignore
                                                    return Object.assign(hash, { [obj.folderName!]: (hash[obj.folderName] || []).concat(obj) });
                                                }, {})
                                        ).map((items: any, index: number) => {
                                            return (
                                                <div className="mb-4" key={index}>
                                                    <Collapsible
                                                        trigger={
                                                            <div
                                                                onClick={() => setArrowDirection(!arrowDirection)}
                                                                className="flex justify-between items-center mb-4"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <img src={folderName} alt="folder" className="w-[25px]" />
                                                                    <span>{items[0].folderName}</span>
                                                                </div>
                                                                <img
                                                                    src={arrowDown}
                                                                    alt="arrow down"
                                                                    className={`transition-all ease-in-out delay-75 ${arrowDirection ? "rotate-180" : ""}`}
                                                                />
                                                            </div>
                                                        }
                                                    >
                                                        {filesToRender(items).map((data: FileType, index: number) => (
                                                            <div className="mr-4" key={data.id}>
                                                                <UploadItem
                                                                    onSuccess={onSuccessHandler}
                                                                    onFail={onFailHandler}
                                                                    onCancel={onCancelHandler}
                                                                    onPause={onPauseHandler}
                                                                    onPlay={onPlayHandler}
                                                                    transferUploadSpeed={handleTransferUploadSpeed}
                                                                    onChunkUpload={onChunkUpload}
                                                                    abort={abort}
                                                                    data={data}
                                                                    onComplete={onComplete}
                                                                    completeRequest={completeUploadQueue.find((item) => item.id === data.id)}
                                                                />
                                                            </div>
                                                        ))}
                                                    </Collapsible>
                                                </div>
                                            );
                                        })}
                                        {filesToRender(filesToUpload)
                                            .filter((item) => item.folderName === undefined)
                                            .map((data, index) => (
                                                <UploadItem
                                                    key={data.id}
                                                    onSuccess={onSuccessHandler}
                                                    onFail={onFailHandler}
                                                    onCancel={onCancelHandler}
                                                    onPause={onPauseHandler}
                                                    onPlay={onPlayHandler}
                                                    transferUploadSpeed={handleTransferUploadSpeed}
                                                    abort={abort}
                                                    onChunkUpload={onChunkUpload}
                                                    data={data}
                                                    onComplete={onComplete}
                                                    completeRequest={completeUploadQueue.find((item) => item.id === data.id)}
                                                />
                                            ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <ConfirmationModal
                            status={cancelModalStatus}
                            onClose={() => setCancelModalStatus(false)}
                            onSubmit={handleCancelAllUploads}
                            text={
                                <div className="text-center">
                                    <p>آیا از لغو بارگذاری مطمئن هستید؟</p>
                                </div>
                            }
                            modalType={"DELETE"}
                            cancelButtonText="خیر"
                            submitButtonText="بله، لغو شود."
                        />
                    </div>
                </Draggable>
            )}
        </>,
        document.getElementById("upload-root")!
    );
}

export default Upload;
